import { Box, Flex, HStack, Spacer, Text } from '@chakra-ui/react'
import type { FC, FormEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { OperationResult } from 'urql'

import useGetObject from '@app/hooks/useGetObject'
import ChatTextarea from '@app/pages/strategies/components/aiChat/components/chatTextarea'
import PipelineAssistantSelector from '@app/pages/strategies/components/aiChat/components/pipelineAssistantSelector'
import useOpenAiUppyInstance from '@app/pages/strategies/components/aiChat/useOpenAiUppyInstance'
import CancelButton from '@app/shared/uploader/components/cancelButton'
import SubmitButton from '@app/shared/uploader/components/submitButton'
import DoubleloopUploader from '@app/shared/uploader/doubleloopUploader'
import type { PipelineRunMutation } from '@graphql/queries'
import { usePipelineRunMutation } from '@graphql/queries'
import type { ChattableObjectInput, PipelineEnum } from '@graphql/types'

type Props = {
  chattableType: keyof ChattableObjectInput
  chattableId: string
  pipelineId: PipelineEnum
  strategyId?: string | null
  chatId?: string | null
  onSuccess?: (data: OperationResult<PipelineRunMutation>) => void
}

type UppyParameters = Parameters<typeof useOpenAiUppyInstance>['0']
type OnUploadSuccess = UppyParameters['onUploadSuccess']
type OnFileRemoved = UppyParameters['onFileRemoved']

const PromptForm: FC<Props> = ({
  pipelineId,
  chattableType,
  chattableId,
  strategyId = null,
  chatId = null,
  onSuccess = null
}) => {
  const [fileIds, setFileIds] = useState<[string, string][]>([])
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [, runPipeline] = usePipelineRunMutation()
  const [stateIsDisabled, setStateIsDisabled] = useState(false)
  const chat = useGetObject(chatId, 'chat')
  const isProcessing = chat?.processedState === 'processing' || chat?.processedState === 'waiting'
  const error = chat?.processedState === 'error'
  const borderColor = error ? 'error' : 'normal'
  const disabled = stateIsDisabled || isProcessing

  const onUploadSuccess = useCallback<OnUploadSuccess>((file, response) => {
    setFileIds((prevFileIds) => [...prevFileIds, [file.id, response.body.id]])
  }, [])

  const onFileRemoved = useCallback<OnFileRemoved>((file) => {
    setFileIds((prevFileIds) => prevFileIds.filter(([uppyFileId, _]) => uppyFileId !== file.id))
  }, [])

  const uppyInstance = useOpenAiUppyInstance({ onUploadSuccess, onFileRemoved })

  const submitPrompt = useCallback(
    async (prompt: string) => {
      setStateIsDisabled(true)
      uppyInstance?.cancelAll()

      try {
        const result = await runPipeline({
          input: {
            prompt,
            fileIds: fileIds.map(([, openAiFileId]) => openAiFileId),
            chattable: { [chattableType]: { id: chattableId } } as ChattableObjectInput,
            pipelineId,
            strategyId,
            chatId
          }
        })
        setFileIds([])
        onSuccess?.(result)
        return result
      } finally {
        setStateIsDisabled(false)
      }
    },
    [uppyInstance, runPipeline, fileIds, chattableType, chattableId, pipelineId, chatId, strategyId, onSuccess]
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()
      const formEntries = new FormData(e.currentTarget)
      const prompt = formEntries.get('prompt') as string

      if (!prompt) {
        return Promise.resolve()
      }

      inputRef.current.value = ''

      return submitPrompt(prompt)
    },
    [submitPrompt]
  )

  useEffect(() => {
    if (!disabled) {
      inputRef.current.focus()
    }
  }, [disabled, inputRef])

  return (
    <Box w="100%">
      <form onSubmit={onSubmit}>
        <Box pos="relative">
          <Box pos="relative" _after={{ position: 'absolute !important' }}>
            <ChatTextarea
              ref={inputRef}
              borderColor={borderColor}
              required={uppyInstance.getFiles().length === 0}
              disabled={disabled}
            />
          </Box>
          <Flex pos="absolute" zIndex="2" top="3" right="0">
            {isProcessing && <CancelButton chatId={chat.id} px={0} />}
            <SubmitButton disabled={disabled} />
          </Flex>
          {chat?.errorMessage && (
            <Text mt="1" color="error" fontSize="xs">
              {chat.errorMessage}
            </Text>
          )}
          <HStack gap={0}>
            <Spacer />
            <DoubleloopUploader disabled={disabled} uppyInstance={uppyInstance} dropTargetRef={inputRef} />
          </HStack>
          <PipelineAssistantSelector pipelineId={pipelineId} chat={chat} />
        </Box>
      </form>
    </Box>
  )
}

export default PromptForm
